import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Modal } from "react-bootstrap";
import { Button } from "../../components/ui";
import GetData from "../../services/getData";
import logPatientChange from "../../services/patientLogs";
import { buildPatientLogText } from "../../services/patientLogMessages";

/**
 * Страница перехода по ID клиента YClients.
 * Запрашивает бэкенд: найден ли пациент по id_yc.
 * Если найден — редирект на /patient/ID/profile.
 * Если нет — диалог «Создать пациента?»; при «Да» — создание и редирект, при «Нет» — на расписание.
 */
export default function PatientYc() {
    const { t } = useTranslation("patient");
    const { idYc } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fioFromState = location.state?.fio || "";
    const clinicIdFromState = location.state?.clinic_id || "";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [fioInput, setFioInput] = useState(fioFromState);

    const resolvePatient = useCallback(async () => {
        if (!idYc) {
            setError(t("yc.noId"));
            setLoading(false);
            return;
        }
        setError(null);
        setNotFound(false);
        try {
            const res = await GetData({
                module: "patient",
                block: "resolve_yc",
                action: "get",
                id_yc: String(idYc).trim(),
            });
            if (res && res.success && res.id != null) {
                navigate(`/patient/${res.id}/overview`, { replace: true });
                return;
            }
            if (res && res.success && res.not_found) {
                setNotFound(true);
                setShowModal(true);
                return;
            }
            setError(res?.message || t("yc.resolveFailed"));
        } catch (err) {
            setError(t("yc.requestError"));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [idYc, navigate, t]);

    useEffect(() => {
        resolvePatient();
    }, [resolvePatient]);

    useEffect(() => {
        setFioInput((prev) => (prev === "" ? fioFromState : prev));
    }, [fioFromState]);

    const handleCreate = async () => {
        const fio = (fioInput || fioFromState || "").trim();
        if (!fio) {
            setError(t("yc.needFio"));
            return;
        }
        setCreating(true);
        setError(null);
        try {
            const res = await GetData({
                module: "patient",
                block: "resolve_yc",
                action: "create",
                id_yc: String(idYc).trim(),
                fio,
                clinic_id: clinicIdFromState || undefined,
            });
            if (res && res.success && res.id != null) {
                await logPatientChange({
                    idPatient: res.id,
                    text: buildPatientLogText("patient_created_from_yc", {
                        details: `ID YClients: ${String(idYc).trim()}; ФИО: ${fio}`,
                    }),
                });
                setShowModal(false);
                navigate(`/patient/${res.id}/overview`, { replace: true });
                return;
            }
            setError(res?.message || t("yc.createFailed"));
        } catch (err) {
            setError(t("yc.requestError"));
            console.error(err);
        } finally {
            setCreating(false);
        }
    };

    const handleCancel = () => {
        setShowModal(false);
        navigate("/schedule/", { replace: true });
    };

    if (loading) {
        return (
            <Container fluid="md" className="py-5">
                <div className="text-center">{t("yc.loading")}</div>
            </Container>
        );
    }

    if (error && !showModal) {
        return (
            <Container fluid="md" className="py-5">
                <div className="text-center text-danger">{error}</div>
                <div className="text-center mt-2">
                    <Button variant="secondary" onClick={() => navigate("/schedule/")}>
                        {t("yc.backToSchedule")}
                    </Button>
                </div>
            </Container>
        );
    }

    return (
        <>
            {notFound && (
                <Modal show={showModal} onHide={handleCancel} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("yc.notFoundTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t("yc.notFoundBody")}
                    <div className="mt-3">
                        <label className="form-label">{t("layout.fields.fio")}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={fioInput}
                            onChange={(e) => setFioInput(e.target.value)}
                            placeholder={t("yc.fioPlaceholder")}
                        />
                    </div>
                    {error && (
                        <div className="mt-2 text-danger small">{error}</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancel} disabled={creating}>
                        {t("yc.no")}
                    </Button>
                    <Button variant="primary" onClick={handleCreate} disabled={creating}>
                        {creating ? t("yc.creating") : t("yc.yes")}
                    </Button>
                </Modal.Footer>
                </Modal>
            )}
        </>
    );
}
